import { type ReactNode } from 'react'
import { SectionHeadline } from '@/components/SectionHeadline'
import {
  ScreenHeaderProvider,
  ScreenHeaderBorder,
  ScreenHeaderMain,
  ScreenHeaderPersistentTopBar,
  ScreenHeaderPersistentTopBarLayout,
  ScreenHeaderPersistentTopBarTitle,
} from '@/components/ScreenHeader'
import { AccountSubviewScreenHeaderContent } from '../components/AccountSubviewScreenHeaderContent'
import { IconButton } from '@/components/IconButton'
import { links } from '@/utils/link'
import { Button } from '@/components/Button'
import { EmptyState } from '@/components/EmptyState'
import TwoFaOnIllustration from '@/components/SpotIllustration/TwoFaOnIllustration'
import TwoFaOffIllustration from '@/components/SpotIllustration/TwoFaOffIllustration'
import styles from './SecurityView.module.css'
import {
  CardButton,
  CardItemContent,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import { useIsSpacious } from '@/hooks/viewport-hooks'

interface SecurityViewProps {
  children?: ReactNode
}

export function SecurityView({ children }: SecurityViewProps) {
  const isSpacious = useIsSpacious()
  return (
    <>
      {!isSpacious && (
        <ScreenHeaderProvider fadeVariant="aggressive">
          <ScreenHeaderPersistentTopBar>
            <ScreenHeaderPersistentTopBarLayout
              left={
                <IconButton label="Back" to={links.account()} name="back" />
              }
            >
              <ScreenHeaderPersistentTopBarTitle>
                Security details
              </ScreenHeaderPersistentTopBarTitle>
            </ScreenHeaderPersistentTopBarLayout>
          </ScreenHeaderPersistentTopBar>
          <ScreenHeaderMain>
            <AccountSubviewScreenHeaderContent
              kicker="Account"
              title="Security details"
            />
            <ScreenHeaderBorder />
          </ScreenHeaderMain>
        </ScreenHeaderProvider>
      )}
      <main className={styles.root}>{children}</main>
    </>
  )
}

interface MFAEnrollPanelProps {
  onEnrollPress: () => unknown
  activateButtonTestId?: string
}

export function MFAEnrollPanel({
  onEnrollPress,
  activateButtonTestId,
}: MFAEnrollPanelProps) {
  return (
    <div>
      <SectionHeadline>Security</SectionHeadline>
      <EmptyState size="dialog">
        <div className="text-center flex flex-col items-center gap-3xl">
          <TwoFaOffIllustration />

          <div>
            <h3 className="text-headline-lg mb-base">
              Two-factor authentication (2FA) is currently turned off
            </h3>
            <h4 className="text-body-md-light">
              Protect your account with an extra layer of security.
            </h4>
          </div>

          <div className="flex flex-col w-full">
            <Button
              onPress={onEnrollPress}
              label="Activate 2FA"
              testId={activateButtonTestId}
            />
          </div>
        </div>
      </EmptyState>
    </div>
  )
}

export function MFAEnrolledPanel() {
  return (
    <div>
      <SectionHeadline>Security</SectionHeadline>

      <EmptyState size="dialog">
        <div className="text-center flex flex-col items-center gap-3xl">
          <TwoFaOnIllustration />

          <div>
            <h3 className="text-headline-lg mb-base">
              Two-factor authentication (2FA) is currently turned on
            </h3>
            <h4 className="text-body-md-light">
              An extra layer of security was added to your account.
            </h4>
          </div>
        </div>
      </EmptyState>
    </div>
  )
}

interface ResetPasswordSectionProps {
  onPasswordResetPress: () => unknown
  resetPasswordButtonTestId?: string
}

export function ResetPasswordSection({
  onPasswordResetPress,
  resetPasswordButtonTestId,
}: ResetPasswordSectionProps) {
  return (
    <div>
      <CardButton
        onPress={onPasswordResetPress}
        testId={resetPasswordButtonTestId}
      >
        <CardItemContent isInteractive>
          <CardTitle>Reset your password</CardTitle>
          <CardSubtitle variant="sm-description">
            We'll email you a link
          </CardSubtitle>
        </CardItemContent>
      </CardButton>
    </div>
  )
}
